
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCountry } from '@/contexts/country';

export interface Insight {
  id: string;
  title: string;
  slug: string;
  content: string;
  published: boolean;
  created_at: string;
  category_id: string;
  country_code: string;
  agree_percentage: number; 
  total_pollsters: number;  
  linked_poll: string | null;
  stats_status: string | null;
  category: {
    id: string;
    name: string;
    slug: string;
  };
}

export const useFilteredInsights = (
  categoryId?: string | null,
  countryCode?: string | null,
  limit?: number
) => {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { countryCode: contextCountryCode } = useCountry();
  
  useEffect(() => {
    // Use provided countryCode first, then fallback to context's countryCode
    const effectiveCountryCode = countryCode || contextCountryCode || null;
    
    async function fetchInsights() {
      setIsLoading(true);
      setError(null);
      
      try {
        // Query insight_cards table with proper joins for category
        // Include the agree_percentage and total_pollsters fields
        let query = supabase
          .from('insight_cards')
          .select(`
            id, 
            title, 
            slug, 
            description,
            status,
            created_at,
            category_id,
            country_code,
            agree_percentage,
            total_pollsters,
            category:insight_categories(id, name, slug)
          `)
          .eq('status', 'published')
          .order('created_at', { ascending: false });
        
        // Additionally, let's fetch information about linked polls
        // This can help with debugging the card-poll relationship
        const { data: linksData } = await supabase
          .from('poll_card_links')
          .select('card_id, poll_id');
        
        // Create a map of card_id to poll_id for quick lookup
        const cardPollMap = new Map();
        if (linksData) {
          linksData.forEach(link => {
            cardPollMap.set(link.card_id, link.poll_id);
          });
        }
        
        if (effectiveCountryCode) {
          query = query.eq('country_code', effectiveCountryCode);
        }
        
        if (categoryId) {
          query = query.eq('category_id', categoryId);
        }
        
        if (limit && limit > 0) {
          query = query.limit(limit);
        }
        
        const { data, error: fetchError } = await query;
        
        if (fetchError) throw fetchError;
        
        // Safely transform the data to match the Insight interface
        if (data) {
          const transformedInsights: Insight[] = data.map(item => ({
            id: item.id,
            title: item.title,
            slug: item.slug || '',
            content: item.description || '',
            published: item.status === 'published',
            created_at: item.created_at,
            category_id: item.category_id || '',
            country_code: item.country_code || '',
            // Include stats data
            agree_percentage: item.agree_percentage || 0,
            total_pollsters: item.total_pollsters || 0,
            // Add linked poll information
            linked_poll: cardPollMap.get(item.id) || null,
            stats_status: 'pending', // Default to 'pending' since it doesn't exist in the database
            category: item.category || { id: '', name: '', slug: '' }
          }));
          
          // Log insights data for debugging
          console.log('Fetched insights with stats:', transformedInsights);
          
          setInsights(transformedInsights);
        } else {
          setInsights([]);
        }
      } catch (err) {
        console.error('Error fetching insights:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch insights'));
        setInsights([]);
      } finally {
        setIsLoading(false);
      }
    }
    
    fetchInsights();
  }, [countryCode, categoryId, limit, contextCountryCode]);
  
  return { insights, isLoading, error };
};
